import { FC, useCallback, useEffect, useState } from "react"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { useConsumer } from "../../Client/ConsumerContext"
import { PenIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { formatPhoneNumberForSweden } from "../../Orders/Components/Form/constants"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { ConsumerState } from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation"
import consumerInfoStyle from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation.module.css"
import { EditConsumerInvoicingInformation } from "../EditConsumerInvoicingInformation/EditConsumerInvoicingInformation"
import style from "./CustomerPortalConsumerInvoicingInformation.module.css"

type CustomerPortalConsumerInvoicingInformationProps = {
	consumerId: string
}

export type ConsumerBasicInvoicingInformation = {
	type: "Basic"
	email: string
	address: ConsumerInvoicingAddress
	contactPerson?: ConsumerInvoicingContact
}

export type ConsumerInvoicingAddress = {
	street: string
	city: string
	zipCode: string
}

export type ConsumerInvoicingContact = {
	name?: string
	phoneNumber?: string
}

export type ConsumerInvoicingInformation = ConsumerBasicInvoicingInformation

type GetConsumerInvoicingDetails = {
	invoiceInformation?: ConsumerInvoicingInformation
}

export const CustomerPortalConsumerInvoicingInformation: FC<CustomerPortalConsumerInvoicingInformationProps> = (
	props: CustomerPortalConsumerInvoicingInformationProps,
) => {
	const client = useClient()
	const auth = useAuth()
	const consumer = useConsumer()
	const permissions = usePermissions()

	const [invoicingInformation, setInvoicingInformation] = useState<ConsumerInvoicingInformation | "loading" | null>(
		"loading",
	)

	const [edit, setEdit] = useState(false)

	const fetchInvoicingInformation = useCallback(() => {
		setInvoicingInformation("loading")
		API.get<GetConsumerInvoicingDetails>(
			`/customer-portal/consumers-v1/invoicing-information/${client.identifier}/${props.consumerId}`,
			true,
		).then(
			(res) => {
				setInvoicingInformation(res?.invoiceInformation || null)
			},
			() => {
				setInvoicingInformation(null)
			},
		)
	}, [client.identifier, props.consumerId])

	useEffect(() => {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Invoicing_Information_Read)) {
			setInvoicingInformation(null)
			return
		}

		fetchInvoicingInformation()
	}, [fetchInvoicingInformation, permissions])

	function content() {
		if (invoicingInformation === "loading") {
			return (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader />
				</div>
			)
		}

		let canEdit = false

		if (auth.IsLoggedInClient) {
			canEdit = permissions.isAllowed(PermissionAreaLocation.Consumer_Invoicing_Information_Update)
		} else if (auth.IsLoggedInConsumer && consumer) {
			const currentConsumer = auth.Me?.consumers?.find((x) => x.id === consumer.consumerId)

			if (currentConsumer) {
				canEdit =
					permissions.isAllowed(PermissionAreaLocation.Consumer_Invoicing_Information_Update) &&
					currentConsumer.state === ConsumerState.Active &&
					!invoicingInformation
			}
		}

		return (
			<>
				<div className={style.headerAndEditRow} style={{ marginBottom: "20px" }}>
					<span className={style.headerText}>Faktureringsuppgifter</span>
					{canEdit ? (
						<span style={{ marginLeft: "5px" }}>
							<PenIcon
								size={22}
								iconClassName={style.editIcon}
								onClick={() => {
									setEdit(true)
								}}
							/>
						</span>
					) : null}
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>E-post för faktura</label>
					<span className={consumerInfoStyle.textAsInput}>{invoicingInformation?.email}</span>
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>Gata</label>
					<span className={consumerInfoStyle.textAsInput}>{invoicingInformation?.address.street}</span>
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>Postnummer</label>
					<span className={consumerInfoStyle.textAsInput}>{invoicingInformation?.address.zipCode}</span>
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>Postort</label>
					<span className={consumerInfoStyle.textAsInput}>{invoicingInformation?.address.city}</span>
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>Kontaktperson namn</label>
					<span className={consumerInfoStyle.textAsInput}>{invoicingInformation?.contactPerson?.name}</span>
				</div>
				<div className={consumerInfoStyle.textInputWithLabel}>
					<label>Kontaktperson telefon</label>
					<span className={consumerInfoStyle.textAsInput}>
						{formatPhoneNumberForSweden(invoicingInformation?.contactPerson?.phoneNumber)}
					</span>
				</div>
			</>
		)
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Invoicing_Information_Read)) {
		return null
	}

	return (
		<>
			{invoicingInformation !== "loading" &&
			permissions.isAllowed(PermissionAreaLocation.Consumer_Invoicing_Information_Update) &&
			edit ? (
				<EditConsumerInvoicingInformation
					consumerId={props.consumerId}
					invoicingInformation={invoicingInformation}
					onClose={() => setEdit(false)}
					onDone={() => {
						setEdit(false)
						fetchInvoicingInformation()
					}}
				/>
			) : null}
			<div className={style.wrapper}>{content()}</div>
		</>
	)
}
